import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { fetchJson } from '../../../helpers/fetch.js';
import { Row, Col } from "../../bootstrap/index.jsx";
import { Button } from '../../bootstrap/index.jsx';
import Select from '../../common/Select.jsx';
import {FaIcon} from '../../common/Icons.jsx';
import InputDatagrid from './InputDatagrid.jsx';
import OutputDatagrid from './OutputDatagrid.jsx';
import merge from 'deepmerge';
import LoadingIndicator from '../../common/loading_indicator.jsx';
import Help from '../../common/Help.jsx';
import FormulaEditor from '../../datagrid/formula_editor/FormulaEditor.lazy.jsx';
import C from '../../datagrid/formula_editor/C.jsx';

import {
  datagrid_select_options_json_path,
  datagrid_structure_path
} from '~/routes';

const OUTPUT_MODES = {basic: 'basic', advanced: 'advanced'}

const SERVER_SIDE_STORED_KEYS = ['input_defs', 'value_def', 'disaggregation_defs', 'visible_columns', 'project_visibility'];

// TODO: Proper ids, small chance for clash
const randomId = () => `DG${Math.round(Math.random() * 1000000)}`

const INITIAL_STATE = {
  // Server stored data
  input_defs: {},
  value_def: {
    colId: randomId(),
    displayName: '',
    field: null,
    aggFunc: 'sum',
    aggFuncFilters: [],
    aggFilterModel: {},
    filterModel: {},
    mode: OUTPUT_MODES.basic
  },
  disaggregation_defs: {},
  visible_columns: [],
  project_visibility: 'none',

  // Frontend state data
  datagrids: undefined,
  form_options: {
    datagrids: []
  },
  active_datagrid: null,
  mode: OUTPUT_MODES.basic
};

export const PROJECT_VISIBILITY_OPTIONS_MAP = {
  always: 'common_indicator',
  always_readonly: 'common_indicator_submit',
  selectable: 'catalogue_indicator',
  selectable_readonly: 'catalogue_indicator_submit',
  none: 'programme_indicator'
};

const FORMULA_INSTRUCTIONS = (
  <div>
    <p>Formulas can be used to derive new values based on other cells and mathematical functions.</p>
    <h5><b>Referring to another column</b></h5>
    <p>Formula can refer to multiple cells within the same row. To refer to other cell use the name of the column marked with <C>[]</C></p>
    <code>SUM([value]) FILTER (WHERE [Gender] = 'Male')</code>
    <h5><b>Datatypes</b></h5>
    <p>Make sure that the output of the formula matches the selected data type. A text value can be returned using single quotation marks.</p>
    <h5><b>Supported operators & functions</b></h5>
    <p>Click to view details</p>
    Math: <C>+</C>, <C>-</C>, <C>*</C>, <C>/</C><br/>
    Comparison: <C>&lt;</C>, <C>&gt;</C>, <C>&lt;=</C>, <C>&gt;=</C>, <C>&lt;&gt;</C>, <C>!=</C>, <C>=</C><br/>
  </div>
);

function reducer(state, action) {
  // console.log(action, JSON.parse(JSON.stringify(state)))
  switch (action.type) {
    case 'add_input': {
      return {
        ...state,
        input_defs: {...state.input_defs, [action.datagrid_id]: action.input_def},
        datagrids: {...state.datagrids, [action.datagrid_id]: {fetch_status: 'pending'}}
      };
    }
    case 'set_input_filters': {
      return {
        ...state,
        value_def: {...state.value_def, filterModel: action.filterModel},
      };
    }
    case 'update_datagrid': {
      return {
        ...state, 
        datagrids: {...state.datagrids, ...action.datagrid}
      }
    }
    case 'set_form_options': {
      return {
        ...state, 
        form_options: {...state.form_options, [action.key]: action.value}
      }
    }
    case 'set_active_datagrid': {
      return {
        ...state, 
        active_datagrid: action.id
      }
    }
    case 'update_value_def': {
      let dupl = {
        ...state.value_def,
      }

      dupl[action.field] = action.value

      return {
        ...state,
        value_def: dupl
      }
    }
    case 'add_disaggregation': {
      return {
        ...state,
        disaggregation_defs: {
          ...state.disaggregation_defs,
          [randomId()]: {
            colId: randomId(),
            displayName: '',
            field: state.value_def.field,
            aggFunc: state.value_def.aggFunc || 'sum',
            aggFuncFilters: [],
            aggFilterModel: {},
            mode: OUTPUT_MODES.basic
          }
        }
      }
    }
    case 'remove_disaggregation': {
      let dupl = {...state.disaggregation_defs};
      delete dupl[action.disaggregation_id];

      return {
        ...state,
        disaggregation_defs: dupl
      }
    }

    case 'update_disaggregation': {
      let dupl = {...state.disaggregation_defs};
      dupl[action.disaggregation_id][action.field] = action.value;

      return {
        ...state,
        disaggregation_defs: dupl
      }
    }

    case 'update_disaggregation_filters': {
      let dupl = {...state.disaggregation_defs};
      dupl[action.disaggregation_id]['aggFilterModel'] = merge.all(action.value.map(v => JSON.parse(v)))
      dupl[action.disaggregation_id]['aggFuncFilters'] = action.value;

      return {
        ...state,
        disaggregation_defs: dupl
      }
    }

    case 'update_disaggregation_mode': {
      let dupl = {...state.disaggregation_defs};
      dupl[action.disaggregation_id]['mode'] = action.value;
      if (action.value == OUTPUT_MODES.advanced) dupl[action.disaggregation_id]['aggFuncFilters'] = [];

      return {
        ...state,
        disaggregation_defs: dupl
      }
    }

    case 'update_visible_columns': {
      return {
        ...state, 
        visible_columns: action.value
      }
    }

    case 'reset_datagrid': {
      return {
        ...INITIAL_STATE,
        form_options: {...state.form_options}
      }
    }

    case 'update_project_visibility': {
      return {
        ...state,
        project_visibility: action.value
      }
    }
  }
}

const Subheader = ({children}) => {
  return <Col className='mt-2'>
    <h4 style={{textTransform: 'uppercase', marginBottom: '0.25rem'}}>{children}</h4>
    <hr style={{marginTop: 0}} />
  </Col>

}

const selectServerSideStateKeys = (obj) => (
  Object.fromEntries(
    Object.entries(obj)
    .filter(([key]) => SERVER_SIDE_STORED_KEYS.includes(key))
  )
);

const buildInitialState = (serverSideState) => ({
    ...INITIAL_STATE,
    ...selectServerSideStateKeys(serverSideState),
    // Select first datagrid as active && Set the datagrids up for a load
    ...(serverSideState.input_defs ? {
      active_datagrid: Object.keys(serverSideState.input_defs)[0],
      datagrids: Object.fromEntries(Object.entries(serverSideState.input_defs).map(([key, value]) => [key, {fetch_status: 'pending'}]))
    } : {})
})

const InputConfigurator = ({indicator, isTemplate = false, formOptions = {}, projectId = null, isProgramme = true}) => {
  const [state, dispatch] = useReducer(reducer, buildInitialState(indicator));
  const gridRef = useRef();

  // Load datagrid options
  useEffect(() => {
    fetchJson(datagrid_select_options_json_path({project_id: projectId}))
    .then(res => dispatch({type: 'set_form_options', key: 'datagrids', value: res}))
  }, [])

  // Load datagrid coldefs when added
  useEffect(() => {
    Object.keys(state.datagrids || {}).forEach(datagrid_id => {
      if (state.datagrids[datagrid_id]?.fetch_status == 'pending' ) {
        dispatch({type: 'update_datagrid', datagrid: {[datagrid_id]: {fetch_status: 'loading'}}})
        fetchJson(datagrid_structure_path({id: datagrid_id, project_id: projectId, _options: true})).then(res => {
          dispatch({type: 'update_datagrid', datagrid: {[datagrid_id]: {...res, fetch_status: 'finished'}}})
          dispatch({type: 'set_active_datagrid', id: datagrid_id})
        })
      }
    })
  }, [state.datagrids])


  const valueOptionFields = useMemo(
    () => {
      if (!state.active_datagrid) return [];

      return (state.datagrids[state.active_datagrid].columns || [])
        .filter(c => ['integer', 'decimal', 'formula'].includes(c.data_type))
        .map(c => ({value: c.accessor, label: c.name}))
    },
    [state.active_datagrid, state.datagrids]
  )

  const dissaggregationOptionField = useMemo(
    () => {
      if (!state.active_datagrid) return [];

      return (state.datagrids[state.active_datagrid].columns || [])
        .filter(c => c.is_selection)
        .map(c => {
          return c.selection_options.map(opt => (
              {
              value: `{"${c.accessor}":{"values":["${opt.value}"], "filterType":"set"}}`,
              label: `${c.group ? c.group + ' / ' : ''}${c.name} / ${opt.label}`,
              type: 'full'
            }
          ))
        }).flat();
    },
    [state.active_datagrid, state.datagrids]
  )

  const allColumns = useMemo(()=> {
    if (!state.active_datagrid) return [];
    return (state.datagrids[state.active_datagrid].columns || [])
      .map(c => ({value: c.accessor, label: c.name}))
  }, [state.active_datagrid, state.datagrids]);

  const formData = useMemo(() => {
    return <input
      type='hidden' 
      name='datagrid_indicator' 
      value={
        JSON.stringify(selectServerSideStateKeys(state))
      } 
    />
  }, [state.input_defs, state.value_def, state.disaggregation_defs, state.visible_columns, state.project_visibility]);

  const selectedDataGrid = state.datagrids && state.datagrids[state.active_datagrid];

  const inputGrid = useMemo(() => {
    if(state.active_datagrid && state.datagrids[state.active_datagrid]?.fetch_status != 'finished') {
      // Still loading datagrid definitions
      return null;
    }

    return <InputDatagrid
        datagrid={selectedDataGrid}
        dispatch={dispatch}
        formOptions={state.form_options.datagrids}
        filterModel={state.value_def.filterModel}
        projectId={projectId}
        withSideBar={false}
        canResetDatagrid={!indicator?.id}
    />
  },[state.active_datagrid, state.form_options.datagrids, state.datagrids]);

  const outputState = useMemo(() => {
    const {value_def, disaggregation_defs, visible_columns, project_visibility, ...restTop} = state;
    const {displayName: _skip, ...valueClean} = value_def;

    const outputDisaggState = Object.entries(disaggregation_defs).reduce((acc, [key, entry]) => {
        const {displayName: _skipName, ...entryClean} = entry;
        acc[key] = entryClean;
        return acc;
      }, {}
    );

    return {...restTop, value_def: valueClean, disaggregation_defs: outputDisaggState};
  }, [
    // Dependencies: include all value_def/disaggregation_defs fields except displayName
    ...Object.entries(state.value_def).filter(([key]) => key !== 'displayName').map(([key, val]) => val),
    ...Object.values(state.disaggregation_defs).flatMap(entry => Object.entries(entry).filter(([key]) => key !== 'displayName').map(([_, val]) => val)
    ),
    state.input_defs,
    state.datagrids,
    state.mode
  ]);

  const OutputDatagridComponent = useMemo(() => {
    if (!outputState?.active_datagrid) return null;

    return <OutputDatagrid
      gridRef={gridRef}
      state={selectServerSideStateKeys(state)}
      dispatch={dispatch}
      mode={state.value_def.mode}
      timeout={1500}
      context={{
        in_project_context: !isProgramme,
        project_id: projectId
      }}
    />
  }, [outputState]);

  useEffect(() => {
    // Update header names in ag‑grid via api
    const api = gridRef.current?.api;
    if (!api) return;

    const currentDefs = api.getColumnDefs();

    const newDefs = currentDefs.map(def => {
      if (def.colId === state.value_def.colId) {
        return { ...def, headerName: state.value_def.displayName };
      }
      const disagg = Object.values(state.disaggregation_defs).find(d => d.colId === def.colId);
      if (disagg) {
        return { ...def, headerName: disagg.displayName };
      }
      return def;
    });

    api.setGridOption('columnDefs', newDefs);
    api.refreshHeader();
  }, [state.value_def.displayName, state.disaggregation_defs]);

  if(state.active_datagrid && state.datagrids[state.active_datagrid]?.fetch_status != 'finished') {
    // Still loading datagrid definitions
    return <LoadingIndicator />
  }


  return <Row>
    {(formOptions.is_programme && !isTemplate) && <Col className='mb-1'>
      <label className='mb-0'>
        {I18n.t('indicator_type_1')} *
        <Help text={I18n.t('indicator_type_help')} />
      </label>
      <div>
        {formOptions.indicator_project_visibilities.map((v) => (
          <span key={`mode-${v}`} style={{display: 'grid', gridTemplateColumns: '25px 1fr', alignItems: 'center'}}>
            <input
              type='radio'
              name='datagrid_indicator[project_visibility]'
              className='mt-0'
              value={v}
              defaultChecked={v == state.project_visibility}
              onClick={(e) => {
                dispatch({type: 'update_project_visibility', value: v})
              }}
              disabled={!!indicator.id}
            />
            <span>{I18n.t(PROJECT_VISIBILITY_OPTIONS_MAP[v])}</span>
          </span>
        ))}
      </div>
    </Col>}

    <Subheader>{I18n.t('dataset_inputs_source_description')}</Subheader>
    <Col>
      {inputGrid}
    </Col>

    { selectedDataGrid &&
      <Col size='xs-11' className='mt-1'>
        <label className="w-100 mb-0">{I18n.t('filters')}</label>
        <div id='input-datagrid-filter-container'/>
      </Col>
    }

    {state.active_datagrid && <>
      <Subheader>{I18n.t('dataset_outputs')}</Subheader>
      <Col className='mt-2'>
        <div style={{display: 'grid', gridTemplateColumns: state.value_def.mode == OUTPUT_MODES.advanced ? '1fr 2fr 1px' : '1fr 1fr 1fr 120px', gap: '2rem'}}>
          <div>
            <label className='mb-0'>{I18n.t('value_column')}</label>
            <input
              type='text' 
              className='form-control'
              placeholder={I18n.t('name')}
              defaultValue={state.value_def.displayName}
              onChange={e => {
                dispatch({type: 'update_value_def', field: 'displayName', value: e.target.value})
              }} 
            />
            <p className='description mt-0'>{I18n.t('value_column_name_description')}</p>
          </div>

          { state.value_def.mode == OUTPUT_MODES.basic ?
            <>
              <div>
                <label className='mb-0'/>
                <Select
                  defaultValue={state.value_def.field}
                  onChange={(newValue) => {
                    dispatch({type: 'update_value_def',  field: 'field', value: newValue})
                  }}
                  options={valueOptionFields}
                />
                <p className='description mt-0'>{I18n.t('value_column_description')}</p>
              </div>

              <div>
                <label className='mb-0'/>
                <Select
                  defaultValue={state.value_def.aggFunc}
                  onChange={(newValue) => {
                    dispatch({type: 'update_value_def', field: 'aggFunc', value: newValue})
                  }}
                  options={['sum', 'avg', 'count', 'last'].map(o => ({label: o, value: o}))}
                />
                <p className='description mt-0'>{I18n.t('value_aggregation_function_description')}</p>
              </div>

              <div>
                <Button className='mt-2' size='sm' onClick={(e) => dispatch({type: 'update_value_def', field: 'mode', value: OUTPUT_MODES.advanced})}>
                  <FaIcon type={'code'}/>
                </Button>
              </div>
            </>
            :
            <div style={{display: 'grid', gridTemplateColumns: '1fr 100px', gap: '2rem'}}>
              <div>
                <label className='mb-0'>{I18n.t('formula')}</label>
                <FormulaEditor
                  columns={allColumns}
                  defaultValue={state.value_def.formula}
                  customInstructions={FORMULA_INSTRUCTIONS}
                  onChange={(newValue) => {
                    dispatch({type: 'update_value_def', field: 'formula', value: newValue})
                  }}
                />
              </div>

              <div>
                <Button className='mt-2' size='sm' onClick={(e) => dispatch({type: 'update_value_def', field: 'mode', value: OUTPUT_MODES.basic})}>
                  <FaIcon type={'sliders'}/>
                </Button>
              </div>
            </div>
          }
        </div>
      </Col>

      <Col className='mt-2'>
        <label className='mb-0'>{I18n.t('disaggregations')}</label>
        <p className='description mt-0'>{I18n.t('disaggregations_description')}</p>
        {Object.entries(state.disaggregation_defs).map(([k,v])=>({disaggregation_id: k, def: v})).map(({disaggregation_id, def}) => (
          <div key={`disaggregation-${disaggregation_id}`} style={{display: 'grid', gridTemplateColumns: `${def.mode == OUTPUT_MODES.advanced ? '1fr 2fr 1px' : '1fr 1fr 1fr 1fr 120px'}`, gap: '2rem'}} className='mb-1'>
              <input 
                type='text' 
                className='form-control'
                placeholder={I18n.t('name')}
                defaultValue={def.displayName} 
                onChange={e => {
                  dispatch({type: 'update_disaggregation', field: 'displayName', value: e.target.value, disaggregation_id: disaggregation_id})
                }} 
              />
              { def.mode == OUTPUT_MODES.basic ?
                <>
                  <Select
                    defaultValue={def.field}
                    placeholder={I18n.t('column')}
                    onChange={(newValue) => {
                      dispatch({type: 'update_disaggregation', field: 'field', value: newValue, disaggregation_id: disaggregation_id})
                    }}
                    options={valueOptionFields}
                  />
                  <Select
                    defaultValue={def.aggFunc}
                    placeholder={I18n.t('value_aggregation_function')}
                    onChange={(newValue) => {
                      dispatch({type: 'update_disaggregation', field: 'aggFunc', value: newValue, disaggregation_id: disaggregation_id})
                    }}
                    options={['sum', 'avg', 'count', 'last'].map(o => ({label: o, value: o}))}
                  />
                  <Select
                    key={`disaggregation-select-${disaggregation_id}`}
                    defaultValue={def.aggFuncFilters}
                    placeholder={I18n.t('filters')}
                    isMulti={true}
                    onChange={(newValue) => {
                      dispatch({type: 'update_disaggregation_filters', value: newValue, disaggregation_id: disaggregation_id})
                    }}
                    options={dissaggregationOptionField}
                  />
                  <div>
                    <Button type='danger' size='sm' onClick={() => dispatch({type: 'remove_disaggregation', disaggregation_id: disaggregation_id})}>
                      <FaIcon type='trash'/>
                    </Button>

                    <Button className='ml-1' size='sm' onClick={() => dispatch({type: 'update_disaggregation_mode', value: OUTPUT_MODES.advanced, disaggregation_id: disaggregation_id})}>
                      <FaIcon type={'code'}/>
                    </Button>
                  </div>
                </>
                :
                <div style={{gridTemplateColumns: '1fr 100px', gap: '2rem'}}>
                  <FormulaEditor
                    columns={allColumns}
                    defaultValue={def.formula}
                    onChange={(newValue) => {
                      dispatch({type: 'update_disaggregation', field: 'formula', value: newValue, disaggregation_id: disaggregation_id})
                    }}
                  />

                  <div>
                    <Button type='danger' size='sm' onClick={() => dispatch({type: 'remove_disaggregation', disaggregation_id: disaggregation_id})}>
                      <FaIcon type='trash'/>
                    </Button>

                    <Button className='ml-1' size='sm' onClick={() => dispatch({type: 'update_disaggregation_mode', value: OUTPUT_MODES.basic, disaggregation_id: disaggregation_id})}>
                      <FaIcon type={'sliders'}/>
                    </Button>
                  </div>
                </div>
              }
            </div>
        ))}
        <Button 
          type='default'
          size='sm'
          onClick={(e) => {
            dispatch({type: 'add_disaggregation'})
          }}
        >
          {I18n.t('add')}
        </Button>
      </Col>
    <Col className='mt-2'>
      <label className='mb-0'>{I18n.t('visible_columns')}</label>
      <p className='description mt-0'>{I18n.t('datagrid_indicator_visible_columns_description')}</p>
      <Select 
          defaultValue={state.visible_columns}
          placeholder={I18n.t('select') + '...'}
          onChange={(newValue) => {
            dispatch({type: 'update_visible_columns', value: newValue})
          }} 
          options={allColumns}
          isMulti={true}
        />
    </Col>

    <Col className='mt-2'>
      {OutputDatagridComponent}
    </Col>
    </>}

    {formData}
  </Row>

}

export default InputConfigurator;